import { type DispositionData, Logger, type DispositionId, type DispositionListInput } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { dispositionKey, dispositionsKey } from '../keys'

const logger = new Logger('useDispositionCache')
// logger.level = 'verbose'

const sortByOrder = (a: DispositionData, b: DispositionData): number => a.order - b.order

export const useDispositionCache = () => {
  const queryClient = useQueryClient()
  return {
    addItem: (dispositionData: DispositionData) => {
      logger.verbose(`addItem disposition ${dispositionData.dispositionId}`)
      queryClient.setQueryData(dispositionKey(dispositionData.dispositionId), dispositionData)
    },
    appendItem: (dispositionData: DispositionData) => {
      logger.verbose(`appendItem disposition ${dispositionData.dispositionId}`)
      const isParentDisposition = dispositionData.parentOnly ?? false
      queryClient.setQueriesData(
        { queryKey: dispositionsKey(isParentDisposition) },
        (old: DispositionData[] | undefined) => {
          const previous = old || []
          const modified = [...previous, dispositionData]
          modified.sort(sortByOrder)
          return modified
        }
      )
    },
    removeItem: (dispositionId: DispositionId) => {
      logger.verbose(`removeItem disposition ${dispositionId}`)
      queryClient.setQueriesData({ queryKey: dispositionsKey() }, (previous: DispositionData[] | undefined) => {
        if (!previous) return
        const modified = previous.filter((d) => d.dispositionId !== dispositionId)
        modified.sort(sortByOrder)
        return modified
      })
    },
    replaceItem: (dispositionData: DispositionData) => {
      logger.verbose(`replaceItem disposition ${dispositionData.dispositionId}`)
      queryClient.setQueryData(dispositionKey(dispositionData.dispositionId), dispositionData)
      queryClient.setQueriesData({ queryKey: dispositionsKey() }, (old: DispositionData[] | undefined) => {
        const previous = old || []
        const modified = previous.map((d) => (d.dispositionId === dispositionData.dispositionId ? dispositionData : d))
        modified.sort(sortByOrder)
        return modified
      })
    },
    setList: (data: DispositionData[], input?: DispositionListInput) => {
      logger.verbose('setList dispositions')
      const isParentDisposition = input?.parentOnly ?? false
      queryClient.setQueryData(dispositionsKey(isParentDisposition, input), data)
    },
    clearList: () => {
      logger.verbose('invalidateQueries dispositionListKey')
      queryClient.invalidateQueries({ queryKey: dispositionsKey() })
    },
  }
}
